import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertRow } from "./AlertRow";
import { SubmissionDialog } from "./SubmissionDialog";
import { errMsg } from "./ResolveDialog";

const ALL = "__all";
const FILTERS = {
  status: [["unresolved", "Unresolved"], ["open", "Not acknowledged"], ["acknowledged", "Acknowledged"], ["resolved", "Resolved"]],
  severity: [[ALL, "Any priority"], ["urgent", "Urgent"], ["high", "High"], ["normal", "Normal"], ["routine", "Routine"], ["low", "Low"]],
  kind: [[ALL, "Any reason"], ["safety", "Safety response"], ["contact", "Contact requested"], ["review", "Review required"],
    ["decline", "Meaningful decline"], ["engagement", "Repeated non-response"], ["opt_out", "Opted out"], ["delivery_failure", "Email delivery failed"]],
};

function Filter({ value, onChange, options, testId, width = "w-44" }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-9 ${width}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function WorklistPanel({ staff, me, readOnly, highlightId }) {
  const [f, setF] = useState({ status: "unresolved", severity: ALL, kind: ALL, assigned_to: ALL });
  const [rows, setRows] = useState(null);
  const [openSid, setOpenSid] = useState(null);
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ALL));
    if (params.assigned_to === "me") params.assigned_to = me?.id;
    api.get("/checkin-worklist", { params }).then((r) => setRows(r.data)).catch((e) => toast.error("Load failed", { description: errMsg(e) }));
  }, [f, me]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const people = [[ALL, "Anyone"], ["me", "Assigned to me"], ["unassigned", "Unassigned"], ...staff.map((u) => [u.id, u.name])];
  return (
    <div className="space-y-3" data-testid="checkin-worklist">
      <div className="flex flex-wrap gap-2">
        <Filter value={f.status} onChange={set("status")} options={FILTERS.status} testId="worklist-filter-status" />
        <Filter value={f.severity} onChange={set("severity")} options={FILTERS.severity} testId="worklist-filter-priority" />
        <Filter value={f.kind} onChange={set("kind")} options={FILTERS.kind} testId="worklist-filter-reason" width="w-52" />
        <Filter value={f.assigned_to} onChange={set("assigned_to")} options={people} testId="worklist-filter-practitioner" width="w-52" />
      </div>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-zinc-400" /> : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 p-6 text-sm text-zinc-600" data-testid="worklist-empty">Nothing here — routine progress reviews update the dashboard without creating tasks.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <AlertRow key={a.id} alert={a} staff={staff} readOnly={readOnly} highlight={a.id === highlightId}
              onChanged={(n) => setRows((rs) => rs.map((x) => (x.id === n.id ? n : x)))} onOpenSubmission={setOpenSid} />
          ))}
        </div>
      )}
      {openSid && <SubmissionDialog sid={openSid} staff={staff} onClose={() => setOpenSid(null)} onChanged={load} />}
    </div>
  );
}
